import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { CHAT_COLUMN_GAP, ChatStatus, DsChat, SceneChat } from 'dragonmouse-shared/interfaces';
import { selectActiveDesignSystem, selectActiveScene } from 'redux/db/reducers';
import { getRightmostFrameX } from 'redux/db/util';
import store from 'redux/store';
import { v4 as uuid } from 'uuid';
import {
	openDsChatDraft,
	openSceneChatDraft,
	replaceDsChatTabWithDraft,
	replaceSceneChatTabWithDraft,
} from '../actions';

// Starts a brand-new chat as a client-only draft: mint a blank chat, hold it in the
// mode slice, and make it active. Nothing is written to the server, so an unsent draft
// never lands in chat history; it becomes a real chat only once its first message is
// sent. Callers keep their own edit/permission guards; this only mints and activates.
// Returns the minted chat id (null when there is no active scene/DS) so callers can
// place the draft in their own UI immediately.

const mintSceneChatDraft = (): SceneChat | null => {
	const state = store.getState();
	const activeScene = selectActiveScene(state.db, state.mode.activeSceneId);
	if (!activeScene) return null;

	const rightmostFrameX = getRightmostFrameX();
	const now = new Date();
	return {
		id: uuid(),
		sceneId: activeScene.id,
		title: NEW_CHAT_TITLE,
		x: rightmostFrameX !== null ? rightmostFrameX + CHAT_COLUMN_GAP : 0,
		y: 0,
		entries: [],
		metadata: { status: ChatStatus.READY, statusMessage: '' },
		createdAt: now,
		updatedAt: now,
		writeId: '',
	};
};

const mintDsChatDraft = (): DsChat | null => {
	const state = store.getState();
	const activeDesignSystem = selectActiveDesignSystem(state.db, state.mode.activeDesignSystemId);
	if (!activeDesignSystem) return null;

	const now = new Date();
	return {
		id: uuid(),
		designSystemId: activeDesignSystem.id,
		title: NEW_CHAT_TITLE,
		entries: [],
		metadata: { status: ChatStatus.READY, statusMessage: '' },
		createdAt: now,
		updatedAt: now,
		writeId: '',
	};
};

export function startSceneChatDraft(): string | null {
	const draft = mintSceneChatDraft();
	if (!draft) return null;
	store.dispatch(openSceneChatDraft(draft));
	return draft.id;
}

export function startDsChatDraft(): string | null {
	const draft = mintDsChatDraft();
	if (!draft) return null;
	store.dispatch(openDsChatDraft(draft));
	return draft.id;
}

// Swaps the chat in one tab for a fresh draft. The replaced chat keeps its server
// row and stays reachable from history; only its tab is given up.
export function replaceSceneChatWithDraft(targetId: string): void {
	const draft = mintSceneChatDraft();
	if (!draft) return;
	store.dispatch(replaceSceneChatTabWithDraft({ targetId, draft }));
}

export function replaceDsChatWithDraft(targetId: string): void {
	const draft = mintDsChatDraft();
	if (!draft) return;
	store.dispatch(replaceDsChatTabWithDraft({ targetId, draft }));
}
